import { useMemo } from 'react';

import { useDeviceSize } from './useDeviceSize';
import { useScreenSize } from './useScreenSize';
import * as ScreenSizes from '../layout/constants/ScreenSizes';
import { ScreenSizesValue } from '../layout/types';

const sizes: ScreenSizesValue[] = [
  ScreenSizes.XS,
  ScreenSizes.SM,
  ScreenSizes.MD,
  ScreenSizes.LG,
  ScreenSizes.XL,
];

type ResponsiveProps<T> = {
  [Property in keyof T]: {
    [SS in ScreenSizesValue]?: T[Property];
  };
};

export function useResponsiveProps<T>(
  props: ResponsiveProps<T>,
  useDevice = false
) {
  const deviceSize = useDeviceSize();
  const windowSize = useScreenSize();
  const screenSize = useDevice ? deviceSize : windowSize;

  return useMemo(() => {
    return Object.keys(props).reduce((acc, _key) => {
      const key = _key as keyof T;

      if (typeof props[key] !== 'object') return acc;

      let breakpoint: ScreenSizesValue | null = screenSize;

      while (!(key in acc)) {
        if (!breakpoint) {
          break;
        } else if (breakpoint in props[key]) {
          acc[key] = props[key][breakpoint];
        } else {
          breakpoint = sizes[sizes.indexOf(breakpoint) - 1];
        }
      }

      return acc;
    }, {} as Partial<T>);
  }, [screenSize, props]);
}
